import { useEffect, useRef, useState } from 'react'
import { ProjectProps } from '../Project'
import { useTranslation } from 'react-i18next';
import Loading from '../../../components/Loading';
import { TbExternalLink, TbReload } from 'react-icons/tb';
import { DatabaseModel } from '../../../utils/interfaces';
import { getAttributes, hasDuplicates, isValidTypeString, useAsyncEffect, useEventListener } from '../../../utils/functions';
import { Admin, getItem } from '../../../api/API';
import { tables } from '../../../utils/constants';
import { useNavigate, useParams } from 'react-router-dom';
import MainBlock from '../../../components/MainBlock';
import Table from '../../../components/Table';
import AlertWrapper from '../../../components/AlertWrapper';
import Alert from '../../../components/Alert';
import { CgClose } from 'react-icons/cg';
import { Input } from '../../../components/Input';
import { BiTrash } from 'react-icons/bi';
import DropDown from '../../../components/DropDown';
import { HiMiniPencilSquare } from 'react-icons/hi2';
import { BoldHeader3 } from '../../../components/General';
import JSONEditor from '../../../components/JSONEditor';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { json2csv } from 'json-2-csv';

const DatabaseManage = (props: ProjectProps) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [database, setDatabase] = useState<DatabaseModel | null>(null);
  const [items, setItems] = useState<Record<string, any>[]>([]);
  const [attributes, setAttributes] = useState<{ name: string, type: 'string' | 'number' | 'list' | 'boolean' | 'map' }[]>([]);
  const [itemInfos, setItemInfos] = useState<Record<string, any>[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // page
  const limit = 100;
  const [page, setPage] = useState(-1);
  const [totalPages, setTotalPages] = useState(1);

  const [showDeleteItemsAlert, setShowDeleteItemsAlert] = useState(false);
  // const [showExportJSON, setShowExportJSON] = useState(false);
  const [showAddItemSidebar, setShowAddItemSidebar] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tempItem, setTempItem] = useState<{ name: string, type: 'string' | 'number' | 'list' | 'boolean' | 'map', value: string }[]>([]);
  const [showAddAttrDropdown, setShowAddAttrDropdown] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // editing attr with json
  const [editingAttr, setEditingAttr] = useState<{ index: number, type: 'list' | 'map', value: string } | null>(null);
  // editing item with json
  const [editingItemString, setEditingItemString] = useState<string | null>(null);
  // import
  const [showImportFrom, setShowImportFrom] = useState(false);
  const [showImportJson, setShowImportJson] = useState(false);
  const [showImportCsv, setShowImportCsv] = useState(false);
  const [newJSON, setNewJSON] = useState<string>('');
  const [isSavingImport, setIsSavingImport] = useState(false);
  // export
  const [showExportTo, setShowExportTo] = useState(false);

  useEffect(() => setNewJSON(''), [showImportJson]);

  useEffect(() => {
    let filtered = [...items].slice(page * limit, (page + 1) * limit);
    setItemInfos(filtered);
    setAttributes([{ name: 'id', type: 'string' }, ...getAttributes(items).filter(attr => attr.name !== 'id')]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, page]);

  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.delete-items-button') && !target.closest('.delete-items-alert')) { // delete alert
      setShowDeleteItemsAlert(false);
    }
    if (!target.closest('#export-to-dropdown') && !target.closest('.export-to-button')) { // export dropdown
      setShowExportTo(false);
    }
    if (!target.closest('#import-from-dropdown') && !target.closest('.import-from-button')) { // import dropdown
      setShowImportFrom(false);
    }
    if (!target.closest('.import-csv-alert')) { // import csv alert
      setShowImportCsv(false);
    }
    if (!target.closest('#import-from-dropdown') && !target.closest('.import-json-alert')) { // import json alert
      setShowImportJson(false);
    }
    if (!target.closest('.add-attr-button') && !target.closest('#add-attr-dropdown')) { // add attribute dropdown
      setShowAddAttrDropdown(false);
    }
    if (!target.closest('.json-editor') && !target.closest('.json-edit-button')) { // json editor
      setTimeout(() => setEditingAttr(null), 10);
      setTimeout(() => setEditingItemString(null), 10);
    }
  }, [editingAttr]);

  // load items
  useAsyncEffect(async () => {
    const db = await getItem(tables.databases, `${props.project.id}_${params.serviceName}`);
    if (!db.item) {
      navigate('/pagenotfound');
      return;
    }
    setDatabase(db.item);
    await initLoadItems(db.item.id);
  }, [props.project, params]);

  // pre-load next page
  useAsyncEffect(async () => {
    if (!database || page < totalPages - 1 || page === -1) return;
    const nextPageData = await Admin.getItems(database.id, undefined, limit, (page + 1) * limit);
    setItems([...items, ...(nextPageData.items || [])]);
    setTotalPages((nextPageData.items || []).length === 0 ? page + 1 : page + 2);
  }, [database, page]);

  // init load items
  const initLoadItems = async (dbId: string) => {
    setIsLoading(true);
    const itemsData = await Admin.getItems(dbId, undefined, limit, 0);
    setItems(itemsData.items || []);
    setPage(0);
    setIsLoading(false);
  }

  // check if item is valid
  const isValidItem = () => {
    if (!tempItem.length) return false;
    for (const info of tempItem) {
      if (!isValidTypeString(info.type, info.value)) return false;
    }
    return true;
  }

  // get json from temp item array
  const arrayToJson = (tempItem: { name: string, type: 'string' | 'number' | 'list' | 'boolean' | 'map', value: string }[]) => {
    let newItem: Record<string, any> = {};
    for (const info of tempItem) {
      newItem[info.name] = info.type === 'string' || !isValidTypeString(info.type, info.value) ? info.value : JSON.parse(info.value);
    }
    return JSON.stringify(newItem, null, 4);
  }

  const updateJsonItem = () => {
    const json = JSON.parse(editingItemString!);
    const newTempItem: { name: string, type: 'string' | 'number' | 'list' | 'boolean' | 'map', value: string }[] = getAttributes([json]).map(attr => ({ name: attr.name, type: attr.name === 'id' ? 'string' : attr.type, value: attr.type === 'string' ? json[attr.name] : JSON.stringify(json[attr.name]) }));
    setTempItem(newTempItem.find(attr => attr.name === 'id') ? newTempItem : [{ name: 'id', type: 'string', value: '' }, ...newTempItem]);
    setTimeout(() => setEditingItemString(null), 10);
  }

  // save json editor
  const saveAttr = () => {
    if (!editingAttr) return;
    setTempItem(tempItem.map((info, index) => index === editingAttr.index ? { ...info, value: JSON.stringify(JSON.parse(editingAttr.value)) } : info));
    setTimeout(() => setEditingAttr(null), 10);
  }

  // on csv file change
  const onCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const csv = e.target?.result as string;
      const lines = csv.split("\n").map((line) => line.trim());
      const headers = lines[0].split(",").map((header) => header.slice(1, -1));
      
      const json = lines.slice(1).map(line => {
        const values = line.split(",").map((value) => value.replace(/""/g, "\"").slice(1, -1));
        const obj: Record<string, any> = {};
        for (let i = 0; i < headers.length; i++) {
          obj[headers[i]] = ['boolean', 'number', 'list', 'map'].some(type => isValidTypeString(type as any, values[i])) ? JSON.parse(values[i]) : values[i];
        }
        return obj;
      });
      setNewJSON(JSON.stringify(json, null, 4));
      setShowImportCsv(true);
    };
    reader.readAsText(file);
  }

  // delete items
  const deleteItems = async () => {
    setIsDeleting(true);
    await Admin.deleteItems(database!.id, selected);
    setItems(items.filter(item => !selected.includes(item.id)));
    setSelected([]);
    setIsDeleting(false);
    setShowDeleteItemsAlert(false);
  }

  // add new item
  const addItem = async () => {
    if (!database) return;
    setIsAdding(true);
    let newItem: Record<string, any> = {};
    for (const info of tempItem) {
      newItem[info.name] = info.type === 'string' ? info.value : JSON.parse(info.value);
    }
    if (items.find(item => item.id === newItem.id)) {
      const { id: _, ...changes } = newItem;
      await Admin.updateItem(database.id, newItem.id, changes);
    } else {
      await Admin.createItem(database.id, newItem);
    }
    setItems([...items, newItem]);
    setIsAdding(false);
    setShowAddItemSidebar(false);
  }

  // save update
  const saveItem = async () => {
    if (!database || !editingId) return;
    setIsSaving(true);
    let newItem: Record<string, any> = {};
    for (const info of tempItem) {
      newItem[info.name] = info.type === 'string' ? info.value : JSON.parse(info.value);
    }
    // if id is changed
    if (editingId !== tempItem[0].value) {
      await Admin.createItem(database.id, newItem);
      await Admin.deleteItems(database.id, [editingId]);
    } else {
      const { id: _, ...changes } = newItem;
      await Admin.updateItem(database.id, editingId, changes);
    }
    setItems(items.map(item => item.id === editingId ? newItem : item));
    setIsSaving(false);
    setEditingId(null);
  }

  // import json
  const importJson = async () => {
    setIsSavingImport(true);
    const json = JSON.parse(newJSON);
    const newItems = Array.isArray(json) ? json : [json];
    await Admin.createItems(database!.id, newItems);
    setItems([...items, ...newItems]);
    setNewJSON('');
    setIsSavingImport(false);
    setShowImportJson(false);
    setShowImportCsv(false);
  }

  // export to
  const exportTo = async (type: 'JSON' | 'CSV') => {
    if (!database || selected.length === 0) return;
    let content = '';
    let blob: Blob;
    content = JSON.stringify(selected.map(id => items.find(item => item.id === id)), null, 4);
    if (type === 'CSV') {
      content = json2csv(selected.map(id => items.find(item => item.id === id)!));
    }
    blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${database.name}.${type.toLowerCase()}`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    isLoading ? <main className='flex items-center justify-center flex-1'><Loading solid size={8} /></main>
      : <>
        <div className='flex-1 flex flex-col p-4 md:px-8 overflow-auto'>
          <div className='flex items-center justify-between overflow-hidden gap-4'>
            <div className='flex items-center flex-1 overflow-hidden'>
              <button onClick={() => navigate(`/project/${props.project.id}/database`)} className='h-8 w-8 flex items-center justify-center rounded-full mr-2'><i className='fa-solid fa-arrow-left' /></button>
              <h1 className='flex-1 md:text-2xl whitespace-nowrap overflow-hidden text-ellipsis'>{database?.name}</h1>
            </div>
          </div>
          <div className='flex-1 overflow-auto mt-4 flex flex-col'>
            <MainBlock extraclass='flex-1 flex flex-col overflow-auto'>
              <div className='flex items-center justify-between flex-wrap'>
                <div>
                  <BoldHeader3>{t('project.database.all-items')} ({items.length})</BoldHeader3>
                </div>
                {/* actions */}
                <div className='flex my-1 flex-wrap items-center gap-2'>
                  {/* pages */}
                  <button onClick={() => {
                    setPage(-1); setTotalPages(1);
                    setTimeout(async () => await initLoadItems(database?.id!), 10);
                  }} className='flex items-center justify-center w-6 h-6 border rounded-md mx-2'><TbReload /></button>
                  <div className='flex items-center'>
                    <button onClick={() => setPage(page - 1)} disabled={page === 0} className='disabled:opacity-30 flex items-center justify-center w-6 h-6 border rounded-md'><LuChevronLeft className='text-lg' /></button>
                    <div className='mx-2'>{page + 1}</div>
                    <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1} className='disabled:opacity-30 flex items-center justify-center w-6 h-6 border rounded-md'><LuChevronRight className='text-lg' /></button>
                  </div>
                  {/* delete */}
                  <button onClick={() => setShowDeleteItemsAlert(true)} disabled={selected.length === 0} className='delete-items-button disabled:opacity-30 bg-gray-100 rounded-lg p-2 px-4 text-xs'>{t('project.database.delete-items')}</button>
                  {/* export */}
                  <div className='relative'>
                    <button onClick={() => setShowExportTo(true)} disabled={selected.length === 0} className='export-to-button disabled:opacity-30 bg-gray-100 rounded-lg p-2 px-4 text-xs'>{t('project.database.export-to')}</button>
                    {showExportTo && <DropDown id='export-to-dropdown' buttonStyle={{ textAlign: 'left', fontSize: '0.8rem' }} options={['JSON', 'CSV']} onSelect={value => { exportTo(value as 'JSON' | 'CSV'); setShowExportTo(false); }} />}
                  </div>
                  {/* import */}
                  <div className='relative'>
                    <button onClick={() => setShowImportFrom(true)} className='import-from-button bg-gray-100 rounded-lg p-2 px-4 text-xs'>{t('project.database.import-from')}</button>
                    <input type='file' accept='.csv' ref={fileInputRef} onChange={onCsvFileChange} style={{ display: 'none' }} />
                    {showImportFrom && <DropDown id='import-from-dropdown' buttonStyle={{ textAlign: 'left', fontSize: '0.8rem' }} options={['JSON', 'CSV']} onSelect={(value) => {
                      (() => value === 'JSON' ? setShowImportJson(true) : fileInputRef.current?.click())();
                      setShowImportFrom(false);
                    }} />}
                  </div>
                  {/* add */}
                  <button onClick={() => {
                    setTempItem([{ name: 'id', type: 'string', value: '' }, ...getAttributes(items).filter(attr => attr.name !== 'id').map(attr => ({ name: attr.name, type: attr.type, value: attr.type === 'boolean' ? 'true' : '' }))]);
                    setShowAddItemSidebar(true);
                  }} className='add-item-button bg-gray-100 rounded-lg p-2 px-4 text-xs'>{t('project.database.add-item')}</button>
                </div>
              </div>
              <div className='flex flex-col flex-1 overflow-auto mt-2'>
                <Table
                  data={itemInfos}
                  setData={setItemInfos}
                  selected={selected}
                  setSelected={setSelected}
                  attrKey={'id'}
                  isKeyBotton
                  valueTypes={{ [t('table-attr.general.size')]: 'byte' }}
                  onClick={(e, item) => {
                    if (item.id === editingId) return;
                    setEditingId(item.id);
                    const infos = attributes.filter(attr => Object.keys(item).includes(attr.name)).map(attr => ({ name: attr.name, type: attr.type, value: attr.type === 'string' ? item[attr.name] : JSON.stringify(item[attr.name]) }));
                    setTempItem([infos.find(info => info.name === 'id')!, ...infos.filter(info => info.name !== 'id')]);
                  }}
                  attributes={attributes}
                />
                {itemInfos.length === 0 && <div className='flex-1 flex items-center justify-center font-light text-sm'>{t('project.database.no-item')}</div>}
              </div>
            </MainBlock>
          </div>
        </div>

        {/* delete items alert */}
        {showDeleteItemsAlert && <AlertWrapper>
          <Alert
            extraclass='delete-items-alert'
            title={t('project.database.delete-items')}
            maxWidth={480}
            onClose={() => setShowDeleteItemsAlert(false)}
            body={<div className='mx-1'>
              <div>{t(selected.length === 1 ? 'project.database.delete-item-message' : 'project.database.delete-items-message', { count: selected.length })}</div>
            </div>}
            bottom={<div className='flex'>
              <button onClick={deleteItems} disabled={selected.length === 0 || isDeleting} className='disabled:bg-gray-300 bg-primary text-white text-sm p-2 px-8 rounded-lg'>{isDeleting ? <Loading /> : t('general.delete')}</button>
            </div>}
          />
        </AlertWrapper>}

        {/* add item */}
        <div className='add-item-sidebar animate z-20 fixed border w-full max-w-120 rounded-xl flex flex-col overflow-auto bg-white h-menu md:h-menu-md bottom-0' style={{ right: showAddItemSidebar ? 0 : '-110%', boxShadow: '0px 0px 8px 2px #0001' }}>
          <div className='sticky top-0 p-4 flex items-center justify-between border-b'>
            <div className='px-2'>{t('project.database.add-item')}</div>
            <button onClick={() => setShowAddItemSidebar(false)} className='hover:bg-gray-100 w-8 h-8 rounded-full flex items-center justify-center'><CgClose /></button>
          </div>
          <div className='flex-1 overflow-auto flex flex-col p-4 gap-4'>
            {(showAddItemSidebar && tempItem) && <>
              <div className='flex justify-between'>
                {/* edit with json */}
                <button onClick={() => setEditingItemString(arrayToJson(tempItem))} className='json-edit-button text-xs underline text-gray-400 px-2 flex items-center'>JSON {t('general.editor')}<HiMiniPencilSquare className='ml-1' /></button>
                {/* add attribute */}
                <div className='relative w-32 flex justify-end'>
                  <button onClick={() => setShowAddAttrDropdown(true)} className='add-attr-button bg-gray-100 rounded-lg p-1.5 px-4 text-xs flex items-center'><i className='fa-solid fa-plus mr-2' />{t('project.database.attr.add')}</button>
                  {showAddAttrDropdown && <DropDown
                    id='add-attr-dropdown'
                    buttonStyle={{ textAlign: 'start', padding: '4px 8px' }}
                    options={[t(`project.database.attr.string`), t(`project.database.attr.number`), t(`project.database.attr.boolean`), t(`project.database.attr.list`), t(`project.database.attr.map`)]}
                    onSelect={attrType => {
                      const typesMap: { [key: string]: 'list' | 'string' | 'number' | 'boolean' | 'map' } = { [t(`project.database.attr.string`)]: 'string', [t(`project.database.attr.number`)]: 'number', [t(`project.database.attr.boolean`)]: 'boolean', [t(`project.database.attr.list`)]: 'list', [t(`project.database.attr.map`)]: 'map' };
                      setTempItem([...tempItem, { name: '', type: typesMap[attrType], value: typesMap[attrType] === 'boolean' ? 'true' : '' }]);
                      setTimeout(() => setShowAddAttrDropdown(false), 10);
                    }}
                  />}
                </div>
              </div>
              {tempItem.map((info, i) => {
                return (
                  <div className='flex gap-2 px-2'>
                    {/* name */}
                    <div className='w-1/4 text-sm'>
                      {i === 0 ? <div className='text-sm mx-1'>id</div>
                        : <Input extraclass={`${(!info.name || tempItem.find((iinfo, j) => i !== j && iinfo.name === info.name)) ? 'border-red-400 focus:border-red-400' : ''}`} textSize='small' value={info.name} onChange={(e) => {
                          setTempItem(tempItem.map((iinfo, j) => i === j ? ({ ...iinfo, name: e.target.value }) : iinfo));
                        }} />}
                      <div className='text-xs font-light m-1'>{t(`project.database.attr.${info.type}`)}</div>
                    </div>
                    {/* value */}
                    {info.type === 'boolean' ? <div className='flex-1'>
                      <div onClick={() => setTempItem(tempItem.map((iinfo, j) => i === j ? ({ ...iinfo, value: iinfo.value === 'true' ? 'false' : 'true' }) : iinfo))} className='h-8 flex items-center px-2 cursor-pointer'>
                        <button className={`flex items-center justify-center w-4 h-4 rounded-full text-white border ${JSON.parse(info.value) ? 'bg-primary border-primary' : 'bg-none'}`} ><i className='fa-solid fa-check text-2xs' /></button>
                        <span className='ml-2 text-sm'>{JSON.parse(info.value) ? t('general.yes') : t('general.no')}</span>
                      </div>
                    </div> : <div className='flex-1'>
                      <Input extraclass={`${(info.name === 'id' && info.value.length > 0) || (info.name !== 'id' && isValidTypeString(info.type, info.value)) ? '' : 'border-red-400 focus:border-red-400'}`} textSize='small' value={info.value} onChange={(e) => setTempItem(tempItem.map((iinfo, j) => i === j ? ({ ...iinfo, value: e.target.value }) : iinfo))} />
                      {/* edit attr with json */}
                      {['list', 'map'].includes(info.type) && <div className='flex justify-end'><button onClick={() => setEditingAttr({ index: i, type: info.type as 'list' | 'map', value: isValidTypeString(info.type, info.value) ? JSON.stringify(JSON.parse(info.value), null, 4) : info.value })} className='json-edit-button flex items-center text-xs text-gray-400 p-1 px-2 underline'>JSON {t('general.editor')}<HiMiniPencilSquare className='ml-1' /></button></div>}
                    </div>}
                    {/* delete */}
                    {i > 0 ? <button onClick={() => setTimeout(() => setTempItem(tempItem.filter((_, j) => i !== j)), 10)} className='flex items-center justify-center w-6 h-8 hover:text-red-400'><BiTrash /></button> : <div className='w-6' />}
                  </div>
                )
              })}
            </>}
          </div>
          <div className='p-4'>
            <button disabled={isAdding ||
              (!!tempItem && (!isValidItem() || // item has invalid value
                !tempItem[0].value || // id is empty
                !!tempItem.find(attr => !attr.name) || // empty attribute name
                hasDuplicates(tempItem, 'name'))) // duplicate attribute name
            } onClick={addItem} className='disabled:bg-gray-300 bg-primary text-white text-sm p-2 px-8 rounded-lg'>{isAdding ? <Loading /> : t('general.save')}</button>
          </div>
        </div>

        {/* edit item */}
        <div className='edit-item-sidebar animate z-20 fixed border w-full max-w-120 rounded-xl flex flex-col overflow-auto bg-white h-menu md:h-menu-md bottom-0' style={{ right: editingId ? 0 : '-110%', boxShadow: '0px 0px 8px 2px #0001' }}>
          <div className='sticky top-0 p-4 flex items-center justify-between border-b'>
            <div className='px-2'>{t('project.database.edit-item')}</div>
            <button onClick={() => setEditingId(null)} className='hover:bg-gray-100 w-8 h-8 rounded-full flex items-center justify-center'><CgClose /></button>
          </div>
          <div className='flex-1 overflow-auto flex flex-col p-4 gap-4'>
            {(editingId && tempItem) && <>
              <div className='flex justify-between'>
                {/* edit with json */}
                <button onClick={() => setEditingItemString(arrayToJson(tempItem))} className='json-edit-button text-xs underline text-gray-400 px-2 flex items-center'>JSON {t('general.editor')}<HiMiniPencilSquare className='ml-1' /></button>
                {/* add attribute */}
                <div className='relative w-32 flex justify-end'>
                  <button onClick={() => setShowAddAttrDropdown(true)} className='add-attr-button bg-gray-100 rounded-lg p-1.5 px-4 text-xs flex items-center'><i className='fa-solid fa-plus mr-2' />{t('project.database.attr.add')}</button>
                  {showAddAttrDropdown && <DropDown
                    id='add-attr-dropdown'
                    buttonStyle={{ textAlign: 'start', padding: '4px 8px' }}
                    options={[t(`project.database.attr.string`), t(`project.database.attr.number`), t(`project.database.attr.boolean`), t(`project.database.attr.list`), t(`project.database.attr.map`)]}
                    onSelect={attrType => {
                      const typesMap: { [key: string]: 'list' | 'string' | 'number' | 'boolean' | 'map' } = { [t(`project.database.attr.string`)]: 'string', [t(`project.database.attr.number`)]: 'number', [t(`project.database.attr.boolean`)]: 'boolean', [t(`project.database.attr.list`)]: 'list', [t(`project.database.attr.map`)]: 'map' };
                      setTempItem([...tempItem, { name: '', type: typesMap[attrType], value: typesMap[attrType] === 'boolean' ? 'true' : '' }]);
                      setTimeout(() => setShowAddAttrDropdown(false), 10);
                    }}
                  />}
                </div>
              </div>
              {tempItem.map((info, i) => {
                return (
                  <div className='flex gap-2 px-2'>
                    {/* name */}
                    <div className='w-1/4 text-sm'>
                      {i === 0 ? <div className='text-sm mx-1'>id</div>
                        : <Input extraclass={`${(!info.name || tempItem.find((iinfo, j) => i !== j && iinfo.name === info.name)) ? 'border-red-400 focus:border-red-400' : ''}`} textSize='small' value={info.name} onChange={(e) => {
                          setTempItem(tempItem.map((iinfo, j) => i === j ? ({ ...iinfo, name: e.target.value }) : iinfo));
                        }} />}
                      <div className='text-xs font-light m-1'>{t(`project.database.attr.${info.type}`)}</div>
                    </div>
                    {/* value */}
                    {info.type === 'boolean' ? <div className='flex-1'>
                      <div onClick={() => setTempItem(tempItem.map((iinfo, j) => i === j ? ({ ...iinfo, value: iinfo.value === 'true' ? 'false' : 'true' }) : iinfo))} className='h-8 flex items-center px-2 cursor-pointer'>
                        <button className={`flex items-center justify-center w-4 h-4 rounded-full text-white border ${JSON.parse(info.value) ? 'bg-primary border-primary' : 'bg-none'}`} ><i className='fa-solid fa-check text-2xs' /></button>
                        <span className='ml-2 text-sm'>{JSON.parse(info.value) ? t('general.yes') : t('general.no')}</span>
                      </div>
                    </div> : <div className='flex-1'>
                      <Input extraclass={`${(info.name === 'id' && info.value.length > 0) || (info.name !== 'id' && isValidTypeString(info.type, info.value)) ? '' : 'border-red-400 focus:border-red-400'}`} textSize='small' value={info.value} onChange={(e) => setTempItem(tempItem.map((iinfo, j) => i === j ? ({ ...iinfo, value: e.target.value }) : iinfo))} />
                      {/* edit attr with json */}
                      {['list', 'map'].includes(info.type) && <div className='flex justify-end'><button onClick={() => setEditingAttr({ index: i, type: info.type as 'list' | 'map', value: isValidTypeString(info.type, info.value) ? JSON.stringify(JSON.parse(info.value), null, 4) : info.value })} className='json-edit-button flex items-center text-xs text-gray-400 p-1 px-2 underline'>JSON {t('general.editor')}<HiMiniPencilSquare className='ml-1' /></button></div>}
                    </div>}
                    {/* delete */}
                    {i > 0 ? <button onClick={() => setTimeout(() => setTempItem(tempItem.filter((_, j) => i !== j)), 10)} className='flex items-center justify-center w-6 h-8 hover:text-red-400'><BiTrash /></button> : <div className='w-6' />}
                  </div>
                )
              })}
            </>}
          </div>
          <div className='p-4'>
            <button disabled={isSaving ||
              (!!tempItem && (!isValidItem() || // item has invalid value
                !tempItem[0].value || // id is empty
                !!tempItem.find(attr => !attr.name) || // empty attribute name
                hasDuplicates(tempItem, 'name'))) // duplicate attribute name
            } onClick={saveItem} className='disabled:bg-gray-300 bg-primary text-white text-sm p-2 px-8 rounded-lg'>{isSaving ? <Loading /> : t('general.save')}</button>
          </div>
        </div>

        {/* edit attr with json */}
        {editingAttr && <AlertWrapper>
          <Alert
            extraclass='json-editor'
            title={'JSON ' + t('general.editor')}
            maxWidth={480}
            onClose={() => setTimeout(() => setEditingAttr(null), 10)}
            body={<>
              <div className='text-xs text-gray-400 m-1'>JSON</div>
              <JSONEditor value={editingAttr.value} setValue={value => setEditingAttr({ ...editingAttr!, value })} />
            </>}
            bottom={<div className='flex'>
              <button onClick={saveAttr} disabled={!isValidTypeString(editingAttr.type, editingAttr.value)} className='disabled:bg-gray-300 bg-primary text-white text-sm p-2 px-8 rounded-lg'>{t('general.save')}</button>
            </div>}
          />
        </AlertWrapper>}

        {/* edit item with json */}
        {editingItemString && <AlertWrapper><Alert
          extraclass='json-editor'
          title={'JSON ' + t('general.editor')}
          maxWidth={480}
          onClose={() => setTimeout(() => setEditingItemString(null), 10)}
          body={<>
            <div className='text-xs text-gray-400 m-1'>JSON</div>
            <JSONEditor value={editingItemString} setValue={value => setEditingItemString(value)} />
          </>}
          bottom={<div className='flex'>
            <button onClick={updateJsonItem} disabled={!isValidTypeString('map', editingItemString)} className='disabled:bg-gray-300 bg-primary text-white text-sm p-2 px-8 rounded-lg'>{t('general.save')}</button>
          </div>}
        />
        </AlertWrapper>}

        {/* import json */}
        {showImportJson && <AlertWrapper>
          <Alert
            extraclass='import-json-alert'
            title={t('project.database.import-from') + ' JSON'}
            maxWidth={480}
            onClose={() => setShowImportJson(false)}
            body={<>
              <div className='text-xs text-gray-400 m-1'>JSON</div>
              <JSONEditor placeholder={`[\n\t{\n\t\t"item1": ...\n\t},\n\t{\n\t\t"item2": ...\n\t},\n\t...\n]`} value={newJSON} setValue={value => setNewJSON(value)} />
            </>}
            bottom={<div className='flex'>
              <button disabled={isSavingImport || !(isValidTypeString('map', newJSON) || isValidTypeString('list', newJSON))} onClick={importJson} className='disabled:bg-gray-300 bg-primary text-white text-sm p-2 px-8 rounded-lg'>{isSavingImport ? <Loading /> : t('general.save')}</button>
            </div>}
          />
        </AlertWrapper>}

        {/* import csv */}
        {showImportCsv && <AlertWrapper>
          <Alert
            extraclass='import-csv-alert'
            title={t('project.database.import-from') + ' CSV'}
            maxWidth={480}
            onClose={() => setShowImportCsv(false)}
            body={<>
              <div className='text-xs text-gray-400 m-1'>JSON</div>
              <JSONEditor value={newJSON} setValue={value => setNewJSON(value)} />
            </>}
            bottom={<div className='flex'>
              <button disabled={isSavingImport || !(isValidTypeString('map', newJSON) || isValidTypeString('list', newJSON))} onClick={importJson} className='disabled:bg-gray-300 bg-primary text-white text-sm p-2 px-8 rounded-lg'>{isSavingImport ? <Loading /> : t('general.save')}</button>
            </div>}
          />
        </AlertWrapper>}
      </>
  )
}

export default DatabaseManage